<template>
  <div
    class="group flex min-w-0 w-full flex-col overflow-hidden rounded-[22px] border border-gray-border bg-white text-gray-dark transition-colors lg:rounded-[18px] lg:hover:border-gray-dark min-[1700px]:flex-row min-[1700px]:items-stretch"
  >
    <div
      class="flex min-w-0 flex-1 flex-col gap-4 px-4 py-4 lg:flex-row lg:items-start lg:gap-4 lg:px-4 lg:py-4 xl:gap-5 xl:px-5 xl:py-5 2xl:gap-6 2xl:px-6"
    >
      <button
        type="button"
        class="flex h-[208px] w-full flex-none cursor-zoom-in items-center justify-center overflow-hidden rounded-[18px] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange/60 lg:h-[clamp(132px,12vw,188px)] lg:w-[clamp(132px,12vw,188px)] lg:self-start lg:rounded-[12px]"
        aria-label="Открыть медиа товара на весь экран"
        @click="openImageModal"
      >
        <img
          :src="imageSrc"
          :alt="title"
          class="max-h-full max-w-full object-contain"
        />
      </button>

      <div class="flex min-w-0 flex-1 flex-col gap-3.5 xl:gap-4">
        <button
          type="button"
          :title="title"
          class="min-w-0 cursor-pointer text-left text-fluid-card text-ellipsis-2 font-helvetica-400 text-gray-dark transition-colors hover:text-orange"
          @click="goodsPage"
        >
          {{ title }}
        </button>

        <div
          class="flex flex-wrap items-center gap-x-4 gap-y-2.5 text-[15px] xl:gap-x-5 2xl:gap-x-6"
        >
          <div class="flex items-center gap-2">
            <span class="text-gray-dark">Марка:</span>
            <span class="truncate font-helvetica-700 text-gray-dark">{{
              brand
            }}</span>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-gray-dark">Артикул:</span>
            <button
              type="button"
              class="flex min-w-0 items-center gap-3 text-left cursor-pointer transition-colors"
              :class="copied ? 'text-gray-light' : 'text-gray-dark'"
              aria-label="Скопировать артикул"
              title="Скопировать артикул"
              @click="copyArticle"
            >
              <span class="truncate font-helvetica-400">{{ article }}</span>
              <SvgoIconComponentGIconCopy class="h-4 w-4 text-gray-light"></SvgoIconComponentGIconCopy>
            </button>
          </div>
        </div>

        <template v-if="availabilityWarehouses.length">
          <button
            type="button"
            class="flex items-center gap-2 self-start rounded-[14px] bg-white-gray px-3.5 py-2 text-[13px] font-helvetica-700 text-gray-dark lg:hidden"
            @click="availabilityOpen = !availabilityOpen"
          >
            <span>В наличии на {{ availableWarehousesCount }} складах</span>
            <SvgoIconComponentFIconFilterarrow class="h-[7px] w-[11px] text-orange transition-transform duration-200" :class="availabilityOpen ? 'rotate-180' : ''"></SvgoIconComponentFIconFilterarrow>
          </button>

          <div v-if="availabilityOpen" class="lg:hidden">
            <GoodsOneAvailability
              v-model:search="availabilitySearch"
              :warehouses="filteredAvailabilityWarehouses"
              :is-minus-disabled="isMinusDisabledAvailability"
              :is-plus-disabled="isPlusDisabledAvailability"
              :is-cart-updating="isCartUpdatingAvailability"
              :show-title="true"
              :show-search="true"
              variant="embedded"
              @change-qty="changeAvailabilityQty"
              @add-to-cart="handleAddToCart"
            />
          </div>

          <div class="hidden lg:block">
            <GoodsOneAvailability
              v-model:search="availabilitySearch"
              :warehouses="filteredAvailabilityWarehouses"
              :is-minus-disabled="isMinusDisabledAvailability"
              :is-plus-disabled="isPlusDisabledAvailability"
              :is-cart-updating="isCartUpdatingAvailability"
              :show-title="true"
              :show-search="true"
              variant="embedded"
              @change-qty="changeAvailabilityQty"
              @add-to-cart="handleAddToCart"
            />
          </div>
        </template>

        <p
          v-if="!warehouseItems.length"
          class="text-[16px] leading-[18px] font-helvetica-400"
          :class="hasStock ? 'text-gray-dark' : 'text-transit-orange'"
        >
          <template v-if="hasStock">
            В наличии
            <component
              :is="isInternalLink(stockLink) ? 'NuxtLink' : 'a'"
              :to="isInternalLink(stockLink) ? stockLink : undefined"
              :href="isInternalLink(stockLink) ? undefined : stockLink || '#'"
              class="cursor-pointer text-orange decoration-transparent underline-offset-2 transition-colors hover:underline hover:decoration-orange"
            >
              на {{ warehousesCount }} {{ warehouseWord }}
            </component>
          </template>
          <template v-else>В пути</template>
        </p>
      </div>
    </div>

    <div
      class="flex flex-wrap items-end gap-x-3 gap-y-3 px-4 pb-4 pt-1 text-right min-[1700px]:w-full min-[1700px]:max-w-[200px] min-[1700px]:flex-col min-[1700px]:flex-nowrap min-[1700px]:items-end min-[1700px]:justify-start min-[1700px]:px-4 min-[1700px]:py-4 xl:px-5 xl:py-5 min-[1920px]:max-w-[220px] 2xl:gap-3.5 2xl:px-6"
    >
      <div
        class="order-2 ml-auto flex shrink-0 gap-2.5 min-[1700px]:order-1 min-[1700px]:ml-0 xl:gap-3"
      >
        <button
          type="button"
          class="rounded-[10px] border border-[rgba(48,48,48,0.16)] bg-white px-4 py-2 text-[14px] font-helvetica-700 text-gray-dark transition-colors hover:border-gray-dark hover:text-gray-dark lg:border-white-gray lg:bg-white-gray lg:px-3 lg:py-2 lg:font-helvetica-400 lg:text-extra-text-color xl:px-[15px] xl:py-2.5 xl:text-[15px] cursor-pointer"
          @click="goodsPage"
        >
          Открыть
        </button>
        <button
          type="button"
          class="group/compare flex h-11 w-11 items-center justify-center rounded-[10px] border border-[rgba(48,48,48,0.16)] bg-white text-gray-dark transition-colors hover:border-gray-dark hover:text-gray-dark lg:h-10 lg:w-10 lg:border-transparent lg:bg-white-gray lg:hover:border-transparent xl:h-[44px] xl:w-[44px]"
          :aria-label="
            isCompared ? 'Удалить из сравнения' : 'Добавить в сравнение'
          "
          :aria-pressed="isCompared"
          @click="addToCompareProduct"
          :disabled="isCompareDisabled"
        >
          <SvgoIconComponentHIconCompare :class="[
              'h-[22px] w-[22px] transition-colors cursor-pointer',
              isCompared
                ? 'text-[var(--color-selected-orange)]'
                : 'text-gray-light lg:group-hover/compare:text-gray-dark',
            ]"></SvgoIconComponentHIconCompare>
        </button>
        <button
          type="button"
          :aria-label="
            isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'
          "
          :aria-pressed="isFavorite"
          @click="toggleFavorite"
          class="group/fav flex h-11 w-11 items-center justify-center rounded-[10px] border border-[rgba(48,48,48,0.16)] bg-white text-gray-dark transition-colors hover:border-gray-dark lg:h-10 lg:w-10 lg:border-transparent lg:bg-white-gray lg:hover:border-transparent xl:h-[44px] xl:w-[44px]"
          :disabled="isToggling || !id"
        >
          <SvgoIconComponentGIconStar :class="[
              'h-[24px] w-[24px] transition-colors cursor-pointer',
              isFavorite
                ? 'text-[var(--color-selected-orange)] favorite-icon-filled'
                : 'text-gray-light lg:group-hover/fav:text-gray-dark',
            ]"></SvgoIconComponentGIconStar>
        </button>
      </div>

      <div
        class="order-1 flex min-w-max flex-col items-start text-left min-[1700px]:order-2 min-[1700px]:min-w-0 min-[1700px]:items-end min-[1700px]:text-right"
      >
        <div
          class="inline-flex min-w-max flex-nowrap items-center gap-x-2 min-[1700px]:justify-end"
        >
          <p
            class="shrink-0 whitespace-nowrap text-[18px] leading-[24px] font-helvetica-700 text-gray-dark font-bold xl:text-[20px] xl:leading-[26px] 2xl:text-[21px] 2xl:leading-[27px]"
          >
            {{ formattedPrice }}&nbsp;₽
          </p>
          <DiscountPercentBadge
            v-if="resolvedPriceBadgeLabel || resolvedDiscountPercent !== null"
            :percent="resolvedDiscountPercent"
            :label="resolvedPriceBadgeLabel"
            size="md"
          />
        </div>
        <p
          v-if="hasDiscountPrice"
          class="whitespace-nowrap text-[16px] leading-[1.2] font-helvetica-700 font-bold text-extra-text-color line-through decoration-[var(--color-extra-text-color)] xl:text-[17px]"
        >
          {{ formattedDiscountPrice }}&nbsp;₽
        </p>
        <p class="text-[14px] text-gray-dark" v-if="unit">{{ unit }}</p>
      </div>
    </div>
  </div>

  <ImageFullscreenModal
    v-model="isImageModalOpen"
    :image-src="imageSrc"
    :media-srcs="modalMediaSources"
    :alt="title"
    v-model:selected-index="modalSelectedIndex"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useNuxtApp } from "#imports";
import DiscountPercentBadge from "~/components/DiscountPercentBadge.vue";
import ImageFullscreenModal from "~/components/ImageFullscreenModal.vue";
import GoodsOneAvailability from "~/components/goods-one/GoodsOneAvailability.vue";
import { useCompareAction } from "~/composables/useCompareAction";
import { useCartStore } from "~/stores/cartStore";
import { normalizeMediaUrl } from "~/utils/normalizeMediaUrl";
import { getDiscountPercent } from "~/utils/pricing";
import { isTransitWarehouse } from "~/utils/warehouses";

const props = withDefaults(
  defineProps<{
    id: string | number;
    title: string;
    brand: string;
    article: string;
    price: number | string;
    discountPrice?: number | string;
    discountPercent?: number | string;
    priceBadgeLabel?: string;
    image: string;
    unit?: string;
    warehouses?: number | string;
    stockLink?: string;
    isFavorite: boolean;
    warehousesList?: any[];
    media?: string[];
  }>(),
  {
    unit: "за штуку",
    warehouses: 0,
    stockLink: "",
    isInCart: false,
  },
);

const router = useRouter();
const route = useRoute();

const goodsPage = () => {
  router.push({
    path: `/goods/${props.id}`,
    query: {
      returnTo: route.fullPath,
    },
  });
};

const isInternalLink = (value?: string) =>
  typeof value === "string" && value.startsWith("/");

const imageSrc = computed(() => {
  return normalizeMediaUrl(props.image) || "/logo.webp";
});

const isImageModalOpen = ref(false);
const modalSelectedIndex = ref(0);

const modalMediaSources = computed(() => {
  const seen = new Set<string>();
  return [...(props.media ?? []), imageSrc.value]
    .map((src) => normalizeMediaUrl(src))
    .filter((src): src is string => {
      if (!src || seen.has(src)) return false;
      seen.add(src);
      return true;
    });
});

const imageMediaIndex = computed(() =>
  modalMediaSources.value.findIndex((src) => src === imageSrc.value),
);

const openImageModal = () => {
  modalSelectedIndex.value =
    imageMediaIndex.value === -1 ? 0 : imageMediaIndex.value;
  isImageModalOpen.value = true;
};

const formattedPrice = computed(() =>
  typeof props.price === "number"
    ? props.price.toLocaleString("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : props.price,
);

const calculatedDiscountPercent = computed(() =>
  getDiscountPercent(props.price, props.discountPrice),
);

const explicitDiscountPercent = computed(() => {
  if (props.discountPercent === undefined || props.discountPercent === null) {
    return null;
  }

  const parsed =
    typeof props.discountPercent === "number"
      ? props.discountPercent
      : Number(String(props.discountPercent).replace(",", "."));

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
});

const resolvedDiscountPercent = computed(
  () => explicitDiscountPercent.value ?? calculatedDiscountPercent.value,
);

const resolvedPriceBadgeLabel = computed(
  () => props.priceBadgeLabel?.trim() || "",
);

const hasDiscountPrice = computed(() => resolvedDiscountPercent.value !== null);

const formattedDiscountPrice = computed(() => {
  if (!hasDiscountPrice.value) return "";
  return typeof props.discountPrice === "number"
    ? props.discountPrice.toLocaleString("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : props.discountPrice;
});

type WarehouseItem = {
  id: string;
  name: string;
  available: number;
  reserve: number;
  salesBlocked?: boolean;
  inTransit?: boolean;
};
type AvailabilityWarehouse = {
  id: string | number;
  name: string;
  stock: number;
  reserve: number;
  qty: number;
  inCart?: boolean;
  salesBlocked?: boolean;
  inTransit?: boolean;
};

const parseSalesBlocked = (raw: unknown) => {
  if (raw === "1" || raw === 1 || raw === true) return true;
  const normalized = String(raw ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "yes";
};

const warehouseItems = computed<WarehouseItem[]>(() => {
  if (!Array.isArray(props.warehousesList)) return [];
  return props.warehousesList
    .map((stock: any) => {
      const id =
        stock?.id ??
        stock?.warehouseId ??
        stock?.code ??
        stock?.value ??
        stock?.["КодСклада"];
      const name =
        stock?.name ??
        stock?.warehouseName ??
        stock?.label ??
        stock?.["НаименованиеСклада"] ??
        stock?.["АдресСклада"] ??
        id;
      const availableRaw =
        stock?.available ??
        stock?.quantity ??
        stock?.qty ??
        stock?.["Доступно"] ??
        stock?.["ВНаличии"];
      const reserveRaw =
        stock?.reserve ?? stock?.["Резерв"] ?? stock?.["ВРезерве"];
      const salesBlockedRaw =
        stock?.salesBlocked ??
        stock?.["ЗапретитьПродажи"] ??
        stock?.["SalesBlocked"];
      const available = Number(availableRaw);
      const reserve = Number(reserveRaw);
      return {
        id: id ? String(id) : "",
        name: name ? String(name) : "",
        available: Number.isFinite(available) ? available : 0,
        reserve: Number.isFinite(reserve) ? reserve : 0,
        salesBlocked: parseSalesBlocked(salesBlockedRaw),
        inTransit: isTransitWarehouse(stock),
      };
    })
    .filter((stock) => stock.id && stock.name);
});

const availabilitySearch = ref("");
const availabilityOpen = ref(false);
const inCartOverrides = ref<Record<string, boolean>>({});
const inCartById = computed(() => {
  const map: Record<string, boolean> = {};
  if (!Array.isArray(props.warehousesList)) return map;
  props.warehousesList.forEach((stock: any) => {
    const id =
      stock?.id ??
      stock?.warehouseId ??
      stock?.code ??
      stock?.value ??
      stock?.["КодСклада"];
    if (!id) return;
    const raw = stock?.inCart ?? stock?.["ВКорзине"] ?? stock?.["Корзина"];
    const num = Number(raw);
    const key = String(id);
    const fromApi =
      raw === "1" ||
      raw === 1 ||
      raw === true ||
      (Number.isFinite(num) && num > 0);
    map[key] = inCartOverrides.value[key] ?? fromApi;
  });
  return map;
});

const availabilityWarehouses = computed<AvailabilityWarehouse[]>(() =>
  warehouseItems.value.map((item) => ({
    id: item.id,
    name: item.name,
    stock: item.available,
    reserve: item.reserve,
    qty: quantities.value[item.id] ?? 0,
    inCart: inCartById.value[item.id],
    salesBlocked: item.salesBlocked,
    inTransit: item.inTransit,
  })),
);

const availableWarehousesCount = computed(
  () => availabilityWarehouses.value.filter((item) => item.stock > 0).length,
);

const filteredAvailabilityWarehouses = computed(() => {
  const query = availabilitySearch.value.trim().toLowerCase();
  if (!query) return availabilityWarehouses.value;
  return availabilityWarehouses.value.filter((warehouse) =>
    warehouse.name.toLowerCase().includes(query),
  );
});

const quantities = ref<Record<string, number>>({});
const isAdding = ref<Record<string, boolean>>({});

const syncQuantities = (items: WarehouseItem[]) => {
  const next = { ...quantities.value };
  items.forEach((item) => {
    const current = next[item.id];
    if (!Number.isFinite(current)) {
      next[item.id] = item.available > 0 ? 1 : 0;
      return;
    }
    const currentValue = Number(current);
    if (item.available <= 0) {
      next[item.id] = 0;
      return;
    }
    if (currentValue < 1) next[item.id] = 1;
    if (currentValue > item.available) next[item.id] = item.available;
  });
  quantities.value = next;
};

watch(warehouseItems, (items) => syncQuantities(items), { immediate: true });
watch(
  () => props.warehousesList,
  () => {
    inCartOverrides.value = {};
  },
  { deep: true },
);

const warehousesCount = computed(() => {
  if (warehouseItems.value.length) {
    return warehouseItems.value.filter((item) => item.available > 0).length;
  }
  return Number(props.warehouses) || 0;
});

const hasStock = computed(() => warehousesCount.value > 0);

const warehouseWord = computed(() => {
  const count = warehousesCount.value;
  const lastTwo = count % 100;
  const lastDigit = count % 10;

  if (lastTwo >= 11 && lastTwo <= 19) return "складах";
  if (lastDigit === 1) return "складе";
  return "складах";
});

const copyArticle = async () => {
  copied.value = false;
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard?.writeText &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(props.article);
      copied.value = true;
      setTimeout(() => (copied.value = false), 1200);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = props.article;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1200);
  } catch (error) {
    console.error("Не удалось скопировать артикул", error);
  }
};

const copied = ref(false);

const isFavorite = ref(props.isFavorite);
const isToggling = ref(false);
const isDisabled = computed(() => !props.id);
const {
  isCompared: checkCompared,
  isCompareUpdating: checkCompareUpdating,
  toggleProductInCompare,
} = useCompareAction();
const isCompared = computed(() => checkCompared(props.id));
const isCompareDisabled = computed(
  () => !props.id || checkCompareUpdating(props.id),
);
const cartStore = useCartStore();

const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp?.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;

const toggleFavorite = async () => {
  if (isDisabled.value || isToggling.value) return;
  isToggling.value = true;

  try {
    await $useOneCApi("addProductToFavorites", {
      product_id: props.id,
    });
    isFavorite.value = !isFavorite.value;
  } catch (error) {
    console.error("Не удалось обновить избранное", error);
  } finally {
    isToggling.value = false;
  }
};

const addToCompareProduct = async () => {
  if (isCompareDisabled.value) return;
  await toggleProductInCompare({
    id: props.id,
    currentlyCompared: isCompared.value,
    title: props.title,
    article: props.article,
    brand: props.brand,
    price: props.price,
    discountPrice: props.discountPrice,
    priceBadgeLabel: props.priceBadgeLabel,
    image: imageSrc.value,
    unit: props.unit,
  });
};

const warehouseKey = (warehouse: AvailabilityWarehouse) => String(warehouse.id);

const isMinusDisabledAvailability = (warehouse: AvailabilityWarehouse) => {
  const key = warehouseKey(warehouse);
  const current = quantities.value[key] ?? 0;
  return warehouse.salesBlocked || warehouse.stock <= 0 || current <= 1;
};

const isPlusDisabledAvailability = (warehouse: AvailabilityWarehouse) => {
  const key = warehouseKey(warehouse);
  const current = quantities.value[key] ?? 0;
  return (
    warehouse.salesBlocked || warehouse.stock <= 0 || current >= warehouse.stock
  );
};

const changeAvailabilityQty = (
  warehouse: AvailabilityWarehouse,
  delta: number,
) => {
  changeQuantity(warehouseKey(warehouse), delta, warehouse.stock);
};

const isCartUpdatingAvailability = (warehouse: AvailabilityWarehouse) =>
  Boolean(isAdding.value[warehouseKey(warehouse)]);

const changeQuantity = (id: string, delta: number, maxAvailable: number) => {
  const current = quantities.value[id] ?? 0;
  const next = current + delta;
  if (next < 1) return;
  if (maxAvailable > 0 && next > maxAvailable) return;
  quantities.value = { ...quantities.value, [id]: next };
};

const setIsAdding = (id: string, value: boolean) => {
  isAdding.value = { ...isAdding.value, [id]: value };
};

const handleAddToCart = (warehouse: AvailabilityWarehouse) => {
  const key = warehouseKey(warehouse);
  const stock = warehouseItems.value.find((item) => item.id === key);
  if (!stock || stock.salesBlocked || stock.available <= 0) return;
  addToCart(stock);
};

const addToCart = async (stock: WarehouseItem) => {
  if (
    isDisabled.value ||
    !stock.id ||
    stock.salesBlocked ||
    stock.available <= 0
  )
    return;
  const quantity = quantities.value[stock.id] ?? 0;
  if (quantity < 1 || (stock.available > 0 && quantity > stock.available))
    return;
  if (isAdding.value[stock.id]) return;

  setIsAdding(stock.id, true);
  try {
    if (inCartById.value[stock.id]) {
      await $useOneCApi("deleteProductFromCart", {
        product_id: props.id,
        stock_id: stock.id,
        quantity: "",
      });
      inCartOverrides.value = { ...inCartOverrides.value, [stock.id]: false };
      cartStore.decrement(1);
    } else {
      await $useOneCApi("addProductToCart", {
        product_id: props.id,
        stock_id: stock.id,
        quantity: String(quantity),
      });
      inCartOverrides.value = { ...inCartOverrides.value, [stock.id]: true };
      cartStore.increment(1);
    }
  } catch (error) {
    console.error("Не удалось обновить корзину", error);
  } finally {
    setIsAdding(stock.id, false);
  }
};
</script>

<style scoped>
.favorite-icon-filled :deep(path) {
  fill: currentColor;
  stroke: currentColor;
}
</style>
